import React, { useRef, useEffect } from 'react';

import { Button } from '@material-ui/core';
import Divider from '@material-ui/core/Divider';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { useHistory } from 'react-router-dom';

import AvatarButton from '../../assets/images/avatar_button.svg';
import SelectedAvatarButton from '../../assets/images/avatar_button_selected.svg';
import { ReactComponent as UserSettings } from '../../assets/images/iconsettings.svg';
import { t } from '../../providers';

const useStyles = makeStyles((theme) => ({
    dashboardButton: {
        width: '68px',
        height: '60px',
        margin: '0px',
        float: 'right',
        borderRadius: '0',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    settingButton: {
        marginTop: '13px',
        paddingBottom: '0px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    avatarMenu: {
        position: 'absolute',
        top: '74px',
        right: '24px',
        background: '#ffffff',
        padding: '26px 0px 20px 0px',
        boxShadow: '0px 1px 5px rgb(0 0 0 / 10%), 0px 3px 4px rgb(0 0 0 / 2%), 0px 2px 4px rgb(0 0 0 / 4%)',
        width: '300px',
        maxHeight: '183px',
        borderRadius: '3px',
    },
    userArea: {
        textAlign: 'center',
    },
    userInfo: {
        marginLeft: '0',
        display: 'inline-grid',
    },
    userRole: {
        color: '#4D4D4D',
        lineHeight: '12px',
        letterSpacing: '1.5px',
        fontSize: '0.625rem',
        fontWeight: '400',
    },
    userName: {
        fontSize: '0.875rem',
        fontWeight: '600',
        lineHeight: '16px',
        color: theme.palette.svg.main,
        letterSpacing: '1.5px',
        margin: '0px 40px',
    },
    divider: {
        margin: '15px 0',
    },
    logout: {
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
        height: '36px',
        minWidth: '4.5rem',
        color: '#3F5E20 !important',
        border: '1px solid #D9D9D9',
        fontWeight: '400',
        fontSize: '0.875rem',
        lineHeight: '142%',
        letterSpacing: '0.25px',
        borderRadius: '3px',
        fontStyle: 'normal',
        padding: '5px 15px',
    },
}));

const UserAvatarMenu = ({ user, logout, lang, myAccountLink }) => {
    const classes = useStyles();
    const [open, setOpen] = React.useState(false);
    const accountDetails = user.userDetails && user.userDetails.accountTypeDetails;
    const history = useHistory();

    // set role
    let role = '';
    if (user.userRole) {
        role = user.userRole;
    } else {
        if (user.userType === 'CHILD' && accountDetails) {
            role = accountDetails.personType;
        }
    }

    const handleMenuVisibility = () => {
        setOpen(!open);
    };

    /* catch click outside mobile menu and then hide it */
    const targetComponent = useRef();

    const handleClickOutside = (e) => {
        if (targetComponent.current.contains(e.target)) {
            // inside click --> do nothing
            return;
        }
        // outside click --> close menu
        setOpen(false);
    };

    const clickMyAccount = () => {
        history.push('/my-account/child-accounts');
        setOpen(false);
    };

    const handleEscapeKey = (event) => {
        if (event.key === 'Escape') {
            const avatarBtn = document.getElementById('menu-right');
            setOpen(false);
            avatarBtn.focus();
        }
    };

    const buttonIsFocused = (event) => {
        if (event.target && event.target.id === 'menu-right') {
            setOpen(false);
        }
    };

    useEffect(() => {
        if (open) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleEscapeKey);
            document.addEventListener('focusin', buttonIsFocused);
        } else {
            document.removeEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [open]);
    /* end of catch click outside mobile menu and the hide the section */

    return (
        <div ref={targetComponent}>
            <Button
                aria-haspopup={true}
                aria-controls="avatarMenu"
                id="menu-right"
                aria-expanded={open}
                aria-label={t('header.menu.right')}
                onClick={handleMenuVisibility}
                className={classes.dashboardButton}
                disableFocusRipple={true}
                disableRipple>
                <img aria-hidden="true" src={open ? AvatarButton : SelectedAvatarButton} alt={t('header.menu.right')} />
            </Button>
            {/** mobile menu */}
            {open && (
                <div className={classes.avatarMenu} id="avatarMenu" aria-labelledby="menu-right" style={!open ? { display: 'none' } : {}}>
                    <div className={classes.userArea}>
                        <div className={classes.userInfo}>
                            <Typography aria-label={user.fullName} variant="body2" className={classes.userName}>
                                {user.fullName}
                            </Typography>
                            <Typography aria-label={role} variant="overline" className={classes.userRole}>
                                {role ? t(role.toLowerCase()) : ''}
                            </Typography>
                        </div>
                    </div>
                    <div className={classes.userArea} style={{ minHeight: 20 }}>
                        <div className={classes.userInfo}>
                            <Button disableFocusRipple id="user-settings" onClick={clickMyAccount} className={classes.settingButton}>
                                <UserSettings />
                                <span
                                    style={{
                                        color: '#3F5E20',
                                        fontSize: '0.875rem',
                                        fontWeight: '400',
                                        lineHeight: '142%',
                                        letterSpacing: '0.25px',
                                        marginLeft: '10px',
                                    }}>
                                    {t('my.account')}
                                </span>
                            </Button>
                        </div>
                    </div>
                    <Divider className={classes.divider} />
                    <div className={classes.userArea}>
                        <div className={classes.userInfo}>
                            <Button disableFocusRipple id="logout-mobile" onClick={logout} className={classes.logout}>
                                {t('log.out')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserAvatarMenu;
